import { useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, AlertTriangle, AlertOctagon, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoHint } from '@/components/common/Hint';
import { useNavigate } from 'react-router-dom';

const SEV_META = {
    warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', textCol: 'text-amber-800', iconCol: 'text-amber-600' },
    danger: { icon: AlertOctagon, bg: 'bg-red-50', border: 'border-red-200', textCol: 'text-red-800', iconCol: 'text-red-600' },
    info: { icon: Bell, bg: 'bg-indigo-50', border: 'border-indigo-200', textCol: 'text-indigo-800', iconCol: 'text-indigo-600' },
};

export function AlertsPanel() {
    const navigate = useNavigate();
    const [data, setData] = useState({ alerts: [], counts: {} });
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('hub_alert_dismissed') || '[]'); } catch { return []; }
    });

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await hubAPI.myAlerts();
                if (alive) setData(r.data);
            } catch { /* silent */ }
            finally { if (alive) setLoading(false); }
        };
        load();
        // Recharge à chaque retour de focus/onglet actif — sinon les alertes restent
        // périmées après qu'une tâche/projet a changé de statut ailleurs.
        const onFocus = () => load();
        const onVisibility = () => { if (!document.hidden) load(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            alive = false;
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    const dismiss = (id) => {
        const next = [...dismissed, id];
        setDismissed(next);
        try { sessionStorage.setItem('hub_alert_dismissed', JSON.stringify(next)); } catch { /* ignore */ }
    };

    const visible = (data.alerts || []).filter((a) => !dismissed.includes(a.id));
    if (loading) return null;
    if (visible.length === 0) return null;

    return (
        <Card className="border-l-4 border-l-indigo-500" data-testid="hub-alerts-panel">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-indigo-500" /> Alertes intelligentes
                    <Badge variant="secondary" className="text-[10px]">{visible.length}</Badge>
                    <InfoHint text="Situations à surveiller sur tes projets et tâches. Les alertes sont anti-spam (cooldown de 12 à 72h) et envoyées aussi par email + push web." />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-2">
                    {visible.map((a) => {
                        const meta = SEV_META[a.severity] || SEV_META.info;
                        const Icon = meta.icon;
                        const hasCta = !!a.cta_url;
                        return (
                            <div
                                key={a.id}
                                className={cn(
                                    "group rounded-md border p-2.5 flex items-start gap-2.5 transition-shadow hover:shadow-sm",
                                    meta.bg, meta.border,
                                    hasCta && "cursor-pointer"
                                )}
                                onClick={hasCta ? () => navigate(a.cta_url) : undefined}
                                onKeyDown={hasCta ? (e) => (e.key === 'Enter' || e.key === ' ') && navigate(a.cta_url) : undefined}
                                role={hasCta ? "button" : undefined}
                                tabIndex={hasCta ? 0 : undefined}
                                data-testid={`alert-${a.id}`}
                            >
                                <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", meta.iconCol)} />
                                <div className="flex-1 min-w-0">
                                    <div className={cn("text-xs font-semibold", meta.textCol)}>{a.title}</div>
                                    <div className="text-[11px] text-slate-600 mt-0.5">{a.message}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {a.cta_url && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 text-[10px] gap-1"
                                            onClick={(e) => { e.stopPropagation(); navigate(a.cta_url); }}
                                            data-testid={`alert-cta-${a.id}`}
                                        >
                                            {a.cta_label} <ChevronRight className="w-3 h-3" />
                                        </Button>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); dismiss(a.id); }}
                                        className="text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Masquer"
                                        data-testid={`alert-dismiss-${a.id}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
